import axios from 'axios';
import { store } from '../redux/store';
import { logout, persistTokens } from '../redux/AuthReducer/action';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const forceLogout = (errorCode) => {
    store.dispatch(logout);
    // errorCode (e.g. SESSION_IDLE_TIMEOUT, OFFICER_INACTIVE) lets the login screen show a
    // specific reason instead of a generic "session expired" message — see LoginScreen.js.
    // Falls back to no reason when the backend didn't send one (flag off, or a plain 401).
    const reason = errorCode ? `&reason=${encodeURIComponent(errorCode)}` : '';
    window.location.href = `/?session_expired=true${reason}`;
    sessionStorage.clear();
};

// Single-flight per tab: sessionStorage is already per-tab, so a module-level promise here is
// enough to collapse concurrent 401s from parallel requests into one refresh call instead of a
// refresh-storm (each of which would otherwise try to rotate the same refresh token).
let refreshPromise = null;

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const statusCode = error?.response?.data?.statusCode || error?.response?.status;

        if (statusCode === 401) {
            const stored = (() => {
                try {
                    return JSON.parse(sessionStorage.getItem('tokens') || localStorage.getItem('tokens') || 'null');
                } catch { return null; }
            })();

            // No stored token at all means we're already logged out (e.g. a request that was
            // in flight when the officer clicked Log Out, landing after storage was cleared) —
            // nothing to refresh or force out of, and no specific reason to surface for a 401
            // that isn't actually about the current session.
            if (!stored) {
                return Promise.reject(error);
            }

            // Only Keycloak-session tokens (session_id present) support refresh; when the
            // backend flag is off, tokens never carry it, so this falls straight through to
            // the existing forced-logout behavior below — unchanged from before this feature.
            if (stored?.session_id && stored?.refreshToken && !error.config?._retriedAfterRefresh) {
                try {
                    refreshPromise ??= axios
                        .post(`${API_BASE}/officer/refresh-token`, { refresh_token: stored.refreshToken })
                        .finally(() => { refreshPromise = null; });

                    const { data } = await refreshPromise;
                    const newTokens = data?.data?.tokens;
                    if (!newTokens?.token) throw new Error('Refresh response missing tokens');

                    persistTokens(newTokens);

                    error.config._retriedAfterRefresh = true;
                    error.config.headers = { ...error.config.headers, Authorization: `Bearer ${newTokens.token}` };
                    return axios(error.config);
                } catch (refreshError) {
                    forceLogout(error?.response?.data?.errorCode);
                    return Promise.reject(error);
                }
            }

            forceLogout(error?.response?.data?.errorCode);
        }

        return Promise.reject(error);
    }
);

// Global request interceptor to trim leading/trailing spaces from query parameters
axios.interceptors.request.use((config) => {
  if (config.params) {
    Object.keys(config.params).forEach(key => {
      if (typeof config.params[key] === 'string') {
        config.params[key] = config.params[key].trim();
      }
    });
  }
  
  if (config.url && config.url.includes('?')) {
    const [baseUrl, queryString] = config.url.split('?');
    const params = new URLSearchParams(queryString);
    let updated = false;
    params.forEach((value, key) => {
      const trimmed = value.trim();
      if (trimmed !== value) {
        params.set(key, trimmed);
        updated = true;
      }
    });
    if (updated) {
      config.url = `${baseUrl}?${params.toString()}`;
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});